import * as React from "react";
import { useCallback, useRef } from "react";
import { 
  BarChart3, ArrowLeft, Search, TrendingUp, TrendingDown, 
  Loader2, Calendar, Info, HelpCircle, Target, Award
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper to format dates for input
const formatDateForInput = (date) => {
  return date.toISOString().split('T')[0];
};

// Get default date ranges
const getDefaultDates = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const yearStart = new Date(today.getFullYear(), 0, 1);
  
  return {
    historicalStart: formatDateForInput(yearStart),
    historicalEnd: formatDateForInput(today),
    analysisStart: formatDateForInput(thirtyDaysAgo),
    analysisEnd: formatDateForInput(today)
  };
};

const RatingBadge = ({ rating }) => {
  const getColors = () => {
    if (rating.includes("Top 10%")) return "bg-emerald-100 text-emerald-700 border-emerald-300";
    if (rating.includes("Top 25%")) return "bg-green-100 text-green-700 border-green-300";
    if (rating.includes("Top 50%")) return "bg-yellow-100 text-yellow-700 border-yellow-300";
    if (rating.includes("Bottom 50%")) return "bg-orange-100 text-orange-700 border-orange-300";
    return "bg-red-100 text-red-700 border-red-300";
  };
  
  return (
    <Badge className={`${getColors()} text-xs font-medium`}>
      {rating}
    </Badge>
  );
};

const SummaryCard = ({ title, value, icon: Icon, color, subtext, tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Card className={`border-l-4 ${color} cursor-help transition-all hover:shadow-md`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{title}</p>
                <p className="text-2xl font-bold">{value}</p>
                {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
              </div>
              <Icon className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </TooltipTrigger>
      {tooltip && (
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      )}
    </Tooltip>
  </TooltipProvider>
);

const PercentileInfoPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-[#e8eef3]">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Análisis
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        <div className="text-center mb-8">
          <div className="p-3 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <BarChart3 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            ¿Qué son los Percentiles?
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Los percentiles te ayudan a entender si un día fue bueno o malo comparándolo con tu histórico
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">🎯 El Problema que Resolvemos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-slate-600 mb-2"><strong>Sin percentiles:</strong></p>
              <div className="pl-4 border-l-2 border-slate-300 text-slate-500 italic">
                <p>Tú: "Ayer tuvimos 72,232 impresiones"</p>
                <p>Tu jefe: "¿Eso es bueno o malo?"</p>
                <p>Tú: "Emmm... no sé, déjame revisar..."</p>
              </div>
            </div>
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <p className="text-emerald-700 mb-2"><strong>Con percentiles:</strong></p>
              <div className="pl-4 border-l-2 border-emerald-400 text-emerald-600">
                <p>Tú: "Ayer tuvimos 72,232 impresiones - <strong>Percentil 69.7</strong>"</p>
                <p>Tu jefe: "¿Qué significa?"</p>
                <p>Tú: "Fue mejor que el 69.7% de nuestros días históricos. 
                     Es un día <strong>🟡 Top 50%</strong> - por encima del promedio"</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">📊 Sistema de Semáforos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                <Badge className="bg-emerald-100 text-emerald-700">🟢 Top 10%</Badge>
                <span className="text-sm text-slate-600">Día excepcional - Mejor que el 90% de tus días históricos</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <Badge className="bg-green-100 text-green-700">🟢 Top 25%</Badge>
                <span className="text-sm text-slate-600">Muy buen día - Mejor que el 75% de tus días históricos</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                <Badge className="bg-yellow-100 text-yellow-700">🟡 Top 50%</Badge>
                <span className="text-sm text-slate-600">Día aceptable - Por encima del promedio</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <Badge className="bg-orange-100 text-orange-700">🟠 Bottom 50%</Badge>
                <span className="text-sm text-slate-600">Día bajo - Por debajo del promedio, vale la pena investigar</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                <Badge className="bg-red-100 text-red-700">🔴 Bottom 25%</Badge>
                <span className="text-sm text-slate-600">Día problemático - Requiere atención inmediata</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">📅 Los Rangos de Fechas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-700 mb-2">Período Histórico</h4>
                <p className="text-sm text-blue-600">
                  Este es el "baseline" contra el cual comparamos. Mientras más datos históricos tengas,
                  más precisos serán los percentiles. Recomendamos al menos 3 meses de datos.
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-700 mb-2">Período de Análisis</h4>
                <p className="text-sm text-purple-600">
                  Estos son los días que quieres evaluar. Puede ser ayer, la última semana,
                  o cualquier período que necesites analizar.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">💡 Ejemplo Práctico</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="mb-3">Si configuras:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600">
                <li><strong>Histórico:</strong> 1 enero 2025 - 31 diciembre 2025 (todo el año)</li>
                <li><strong>Análisis:</strong> 1 diciembre - 31 diciembre 2025 (último mes)</li>
              </ul>
              <p className="mt-3 text-slate-700">
                El sistema comparará cada día de diciembre contra TODOS los días del año,
                y te dirá si fue un día Top 10%, Top 25%, etc.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button onClick={onBack} className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
            Volver al Análisis
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PercentileAnalysisView = ({ onBack }) => {
  const [clients, setClients] = React.useState([]);
  const [selectedClient, setSelectedClient] = React.useState("");
  const [analysisResult, setAnalysisResult] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingClients, setIsLoadingClients] = React.useState(true);
  const [showInfoPage, setShowInfoPage] = React.useState(false);
  
  // Date ranges
  const defaultDates = React.useMemo(() => getDefaultDates(), []);
  const [historicalStart, setHistoricalStart] = React.useState(defaultDates.historicalStart);
  const [historicalEnd, setHistoricalEnd] = React.useState(defaultDates.historicalEnd);
  const [analysisStart, setAnalysisStart] = React.useState(defaultDates.analysisStart);
  const [analysisEnd, setAnalysisEnd] = React.useState(defaultDates.analysisEnd);

  // Activity tracking (with duplicate prevention)
  const hasTrackedView = useRef(false);
  const trackActivity = useCallback(async (action, details = null) => {
    try {
      await axios.post(`${API}/track-activity`, {
        action,
        module: "percentile_analysis",
        details
      }, { withCredentials: true });
    } catch (e) { /* silent */ }
  }, []);

  // Track view on mount (only once)
  React.useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackActivity("view");
    }
  }, [trackActivity]);

  React.useEffect(() => {
    fetchClients();
  }, []);

  // Show info page if requested
  if (showInfoPage) {
    return <PercentileInfoPage onBack={() => setShowInfoPage(false)} />;
  }

  const fetchClients = async () => {
    try {
      setIsLoadingClients(true);
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Error al cargar clientes", {
        description: "No se pudieron obtener los clientes de Databricks"
      });
    } finally {
      setIsLoadingClients(false);
    }
  };

  const analyzePercentiles = async () => {
    if (!selectedClient) return;
    
    try {
      setIsLoading(true);
      setAnalysisResult(null);
      
      const response = await axios.post(`${API}/percentiles/analyze`, {
        client_name: selectedClient,
        historical_start: historicalStart,
        historical_end: historicalEnd,
        analysis_start: analysisStart,
        analysis_end: analysisEnd
      });
      
      setAnalysisResult(response.data);
      
      // Track query with full details
      trackActivity("query", {
        client: selectedClient,
        historical_start: historicalStart,
        historical_end: historicalEnd,
        analysis_start: analysisStart,
        analysis_end: analysisEnd,
        total_records: response.data.total_records,
        top_10_count: response.data.top_10_count,
        top_25_count: response.data.top_25_count,
        bottom_50_count: response.data.bottom_50_count
      });
      
      if (response.data.total_records === 0) {
        toast.info("Sin datos", {
          description: "No se encontraron registros para el período seleccionado"
        });
      } else {
        toast.success(`${response.data.total_records} registros analizados`, {
          description: `${response.data.top_10_count + response.data.top_25_count} días excepcionales detectados`
        });
      }
    } catch (error) {
      console.error("Error analyzing:", error);
      toast.error("Error al analizar", {
        description: error.response?.data?.detail || "No se pudo ejecutar el análisis"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Quick date presets - Baseline (Historical)
  const applyBaselinePreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch(preset) {
      case "last_12_months":
        start = new Date(today);
        start.setMonth(start.getMonth() - 12);
        end = today;
        break;
      case "last_6_months":
        start = new Date(today);
        start.setMonth(start.getMonth() - 6);
        end = today;
        break;
      case "last_3_months":
        start = new Date(today);
        start.setMonth(start.getMonth() - 3);
        end = today;
        break;
      case "ytd": // Year to date
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case "last_year":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case "q4_last_year":
        start = new Date(today.getFullYear() - 1, 9, 1); // Oct
        end = new Date(today.getFullYear() - 1, 11, 31); // Dec
        break;
      default:
        return;
    }
    
    setHistoricalStart(formatDateForInput(start));
    setHistoricalEnd(formatDateForInput(end));
  };

  // Quick date presets - Analysis Period
  const applyAnalysisPreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch(preset) {
      case "yesterday":
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        break;
      case "last_7_days":
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        end = today;
        break;
      case "last_14_days":
        start = new Date(today);
        start.setDate(start.getDate() - 14);
        end = today;
        break;
      case "last_30_days":
        start = new Date(today);
        start.setDate(start.getDate() - 30);
        end = today;
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "last_90_days":
        start = new Date(today);
        start.setDate(start.getDate() - 90);
        end = today;
        break;
      default:
        return;
    }
    
    setAnalysisStart(formatDateForInput(start));
    setAnalysisEnd(formatDateForInput(end));
  };

  return (
    <div className="min-h-screen bg-[#e8eef3]">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500">
                  <BarChart3 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-800">Análisis de Percentiles</h1>
                  <p className="text-xs text-slate-500">¿Este día fue bueno o malo?</p>
                </div>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowInfoPage(true)}
              className="gap-2"
            >
              <HelpCircle className="h-4 w-4" />
              ¿Cómo funciona?
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-cyan-500" />
              Configurar Análisis
            </CardTitle>
            <CardDescription>
              Selecciona el cliente y los rangos de fechas para el análisis de percentiles
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Client Selection */}
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select 
                value={selectedClient} 
                onValueChange={setSelectedClient}
                disabled={isLoadingClients}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder={isLoadingClients ? "Cargando clientes..." : "Seleccionar cliente"} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.cliente} value={client.cliente}>
                      {client.cliente} ({client.account_count} cuentas)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Ranges */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Historical Period */}
              <div className="space-y-3 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <Label className="text-blue-700 font-medium">Período Histórico (Baseline)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3.5 w-3.5 text-blue-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs max-w-xs">
                          Datos históricos contra los cuales se calculan los percentiles. 
                          Mientras más datos, más precisos los resultados.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                
                {/* Baseline Presets */}
                <div className="flex flex-wrap gap-1.5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-blue-100 hover:text-blue-700"
                    onClick={() => applyBaselinePreset("last_12_months")}
                  >
                    12 meses
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-blue-100 hover:text-blue-700"
                    onClick={() => applyBaselinePreset("last_6_months")}
                  >
                    6 meses
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-blue-100 hover:text-blue-700"
                    onClick={() => applyBaselinePreset("last_3_months")}
                  >
                    3 meses
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-blue-100 hover:text-blue-700"
                    onClick={() => applyBaselinePreset("ytd")}
                  >
                    YTD
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-blue-100 hover:text-blue-700"
                    onClick={() => applyBaselinePreset("last_year")}
                  >
                    Año pasado
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-blue-100 hover:text-blue-700"
                    onClick={() => applyBaselinePreset("q4_last_year")}
                  >
                    Q4 pasado
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Desde</Label>
                    <Input 
                      type="date" 
                      value={historicalStart}
                      onChange={(e) => setHistoricalStart(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Hasta</Label>
                    <Input 
                      type="date" 
                      value={historicalEnd}
                      onChange={(e) => setHistoricalEnd(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Analysis Period */}
              <div className="space-y-3 p-4 bg-purple-50/50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-500" />
                  <Label className="text-purple-700 font-medium">Período de Análisis</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3.5 w-3.5 text-purple-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs max-w-xs">
                          Días que quieres evaluar. Puede ser ayer, la última semana, 
                          o cualquier período que necesites analizar.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                
                {/* Analysis Presets */}
                <div className="flex flex-wrap gap-1.5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyAnalysisPreset("yesterday")}
                  >
                    Ayer
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyAnalysisPreset("last_7_days")}
                  >
                    7 días
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyAnalysisPreset("last_14_days")}
                  >
                    14 días
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyAnalysisPreset("last_30_days")}
                  >
                    30 días
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyAnalysisPreset("this_month")}
                  >
                    Este mes
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyAnalysisPreset("last_month")}
                  >
                    Mes pasado
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyAnalysisPreset("last_90_days")}
                  >
                    90 días
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Desde</Label>
                    <Input 
                      type="date" 
                      value={analysisStart}
                      onChange={(e) => setAnalysisStart(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Hasta</Label>
                    <Input 
                      type="date" 
                      value={analysisEnd}
                      onChange={(e) => setAnalysisEnd(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Analyze Button */}
            <div className="flex justify-end">
              <Button 
                onClick={analyzePercentiles}
                disabled={!selectedClient || isLoading}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Analizar Percentiles
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {analysisResult && (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-5 gap-4">
              <SummaryCard
                title="Total Registros"
                value={analysisResult.total_records}
                icon={BarChart3}
                color="border-l-slate-400"
                tooltip="Total de días analizados en el período seleccionado"
              />
              <SummaryCard
                title="Top 10%"
                value={analysisResult.top_10_count}
                icon={Award}
                color="border-l-emerald-500"
                subtext="Días excepcionales"
                tooltip="Días que superaron al 90% del histórico"
              />
              <SummaryCard
                title="Top 25%"
                value={analysisResult.top_25_count}
                icon={TrendingUp}
                color="border-l-green-500"
                subtext="Muy buenos días"
                tooltip="Días que superaron al 75% del histórico"
              />
              <SummaryCard
                title="Top 50%"
                value={analysisResult.top_50_count}
                icon={Target}
                color="border-l-yellow-500"
                subtext="Días aceptables"
                tooltip="Días por encima del promedio"
              />
              <SummaryCard
                title="Bottom 50%"
                value={analysisResult.bottom_50_count + analysisResult.bottom_25_count}
                icon={TrendingDown}
                color="border-l-orange-500"
                subtext="Días a revisar"
                tooltip="Días por debajo del promedio - vale la pena investigar"
              />
            </div>

            {/* Period Info */}
            <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
              <CardContent className="py-4">
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-cyan-500" />
                    <span className="text-slate-600">
                      <strong>Histórico:</strong> {analysisResult.historical_period}
                    </span>
                  </div>
                  <div className="h-4 w-px bg-slate-300" />
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-purple-500" />
                    <span className="text-slate-600">
                      <strong>Análisis:</strong> {analysisResult.analysis_period}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results Table */}
            {analysisResult.records.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Resultados por Día</CardTitle>
                      <CardDescription>
                        Cada día comparado contra tu histórico - ordenados por mejor rendimiento
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-medium uppercase">Cuenta</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Fecha</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-right">Impresiones</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-center">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className="flex items-center gap-1 justify-center">
                                  Percentil Imp
                                  <Info className="h-3 w-3 text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Porcentaje de días históricos superados</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase text-center">Rating Imp</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-right">Conversiones</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-center">Percentil Conv</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-center">Rating Conv</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-right">Costo USD</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-right">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className="flex items-center gap-1 justify-end">
                                  Mediana Imp
                                  <Info className="h-3 w-3 text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Valor típico de impresiones (P50)</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analysisResult.records.map((record, idx) => (
                          <TableRow 
                            key={idx} 
                            className={
                              record.rating_impressions.includes("Top 10%") ? "bg-emerald-50/50" :
                              record.rating_impressions.includes("Bottom 25%") ? "bg-red-50/50" : ""
                            }
                          >
                            <TableCell className="font-medium text-sm max-w-[150px] truncate">
                              {record.account_name}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {record.date}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {record.impressions.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="font-mono text-sm">
                                {record.percentile_impressions.toFixed(1)}%
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <RatingBadge rating={record.rating_impressions} />
                            </TableCell>
                            <TableCell className="text-right">
                              {record.conversions.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="font-mono text-sm">
                                {record.percentile_conversions.toFixed(1)}%
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <RatingBadge rating={record.rating_conversions} />
                            </TableCell>
                            <TableCell className="text-right text-slate-600">
                              ${record.cost_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 text-xs">
                              {record.median_impressions.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {analysisResult.records.length === 0 && (
              <Card className="border-slate-200 bg-slate-50/50">
                <CardContent className="py-12 text-center">
                  <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-500">Sin datos</h3>
                  <p className="text-slate-400 mt-2">
                    No se encontraron registros para el período seleccionado. 
                    Intenta con un rango de fechas diferente.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};
